
import { Component, Mixins, Prop, Emit } from "vue-property-decorator";
import { DomainRegistry } from "../../../../../domain";
import { IDocumentImageSource, ImageKinds } from "../../../../../domain/document";
import { MovableImageItem } from "../../../../../domain/document/items";
import { Geometry, Rectangle, Point, Size } from "../../../../../domain/drawing";
import { IDrawableItemEditable } from "../../IDrawableItemEditable";
import {
  DrawableItemEditorMixin,
  CommonComponentsAccessibleMixin,
} from "../../mixins";
import { CanvasDrawerFactory, ICanvasDrawable } from "../canvas";

@Component
export default class DrawableObjectMixin extends Mixins(DrawableItemEditorMixin, CommonComponentsAccessibleMixin) {
  @Prop()
  shouldDrawEditStyle: boolean;

  @Prop()
  shouldDrawTransparentAll: boolean;

  @Prop()
  isFocused: boolean;

  @Prop({ default: 1 })
  scale: number;

  get privateId(): number {
    return this.editingSource.privateId;
  }

  get isVisible(): boolean {
    return this.editingSource.isVisible && !this.editingSource.isDeleted;
  }

  get absoluteGeometry(): Geometry {
    return this.editingSource.geometry;
  }

  get geometry(): Geometry {
    return this.absoluteGeometry.moveTo(new Point(0, 0));
  }

  get rectangle(): Rectangle {
    return this.geometry.rectangle;
  }

  get topLeft(): Point {
    return this.rectangle.topLeft;
  }

  get topRight(): Point {
    return this.rectangle.topRight;
  }

  get bottomLeft(): Point {
    return this.rectangle.bottomLeft;
  }

  get bottomRight(): Point {
    return this.rectangle.bottomRight;
  }

  get center(): Point {
    return this.rectangle.center;
  }

  get size(): Size {
    return this.rectangle.size;
  }

  get x(): number {
    return this.rectangle.x;
  }

  get y(): number {
    return this.rectangle.y;
  }

  get width(): number {
    return this.rectangle.width;
  }

  get height(): number {
    return this.rectangle.height;
  }

  get zIndex(): number {
    return this.geometry.zIndex;
  }

  get absoluteGeometryStyleProperties(): any {
    return this.toGeoemetryStyleProperties(this.absoluteGeometry);
  }

  get geometryStyleProperties(): any {
    return this.toGeoemetryStyleProperties(this.geometry);
  }

  get toolTip(): string {
    return this.editingSource.toolTip;
  }

  get handleSize(): Size {
    return new Size(this.handleWidth, this.handleHeight);
  }

  get handleWidth(): number {
    return this.adjustHandleSize(this.$configuration.behavior.document.editor.handles.size.width);
  }

  get handleHeight(): number {
    return this.adjustHandleSize(this.$configuration.behavior.document.editor.handles.size.height);
  }

  @Emit()
  focus(source: IDrawableItemEditable): void {}

  @Emit()
  loseFocus(source: IDrawableItemEditable): void {}

  emitFocus(): void {
    this.focus(this);
  }

  emitLoseFocus(): void {
    this.loseFocus(this);
  }

  private adjustHandleSize(size: number): number {
    return size / this.scale;
  }

  private toGeoemetryStyleProperties(geometry: Geometry): any {
    const rectangle = geometry.rectangle;
    // プロパティ名をLowerCamelCaseで記載すると、styleの方にはChainCaseで反映される.
    return {
      left: rectangle.x + "px",
      top: rectangle.y + "px",
      width: rectangle.width + "px",
      height: rectangle.height + "px",
    };
  }

  public toModifiedAbsoluteGeoemetryStyleProperties(rectangle: Rectangle): any {
    const modified = this.absoluteGeometry.add(rectangle);
    return this.toGeoemetryStyleProperties(modified);
  }

  public getBoundingClientRectangle(): Rectangle {
    const r = this.$el.getBoundingClientRect();
    return new Rectangle(new Point(Math.floor(r.left), Math.floor(r.top)), this.size);
  }

  public get isImageConvertible(): boolean {
    return this.editingSource.isAdditional
        && !!this.createCanvasDrawer();
  }

  private createCanvasDrawer(): ICanvasDrawable {
    const factory = new CanvasDrawerFactory();
    return factory.create(this.editingSource);
  }

  public async eraseConvertedImage(): Promise<void> {
    const clientRectangle = this.getBoundingClientRectangle();
    const requiredRectangle = this.createCanvasDrawer().getRequiredRectangle();
    const imageSource = await this.$eraseImage(
      new Rectangle(clientRectangle.topLeft.add(requiredRectangle.topLeft), requiredRectangle.size),
      this.scale,
      this.convertToImageSource(requiredRectangle.size),
      this.$el as HTMLElement);
    if (!!imageSource) {
      this.swapToImageItem(await this.createNewImage(imageSource, requiredRectangle));
    }
  }

  private convertToImageSource(size: Size): string {
    const canvas = this.createCanvasElement(size.width, size.height);
    this.drawCanvasElement(canvas);
    return canvas.toDataURL("image/png");
  }

  private createCanvasElement(width: number, height: number): HTMLCanvasElement {
    const element = document.createElement("canvas");
    element.setAttribute("width", width.toString());
    element.setAttribute("height", height.toString());
    element.style.visibility = "hidden";
    element.style.position = "fixed";
    return element;
  }

  private drawCanvasElement(canvas: HTMLCanvasElement): void {
    const drawer = this.createCanvasDrawer();
    if (!drawer) {
      return;
    }
    const context = canvas.getContext("2d");
    drawer.draw(context);
  }

  private async createNewImage(imageSource: IDocumentImageSource, requiredRectangle: Rectangle): Promise<MovableImageItem> {
    const repository = DomainRegistry.document.getDocumentRepository();
    const item = await repository.createNewObjects("NewImage") as MovableImageItem;
    item.geometry.rectangle = new Rectangle(this.editingSource.geometry.rectangle.topLeft.add(requiredRectangle.topLeft), requiredRectangle.size.clone());
    item.imageSource = imageSource;
    item.imageKind = ImageKinds.Image;
    return item;
  }

  private swapToImageItem(item: MovableImageItem): void {
    const editor = this.commonComponents.editors.page.editor;
    editor.removeItem(this.editingSource);
    editor.addItem(item);
  }
}
